import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, PhoneMissed, MessageSquareText, Send, CheckCircle2, RefreshCw, Star } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_STYLES = {
  missed_call: "border-red-400/40 bg-red-400/10 text-red-400",
  sms_sent: "border-[#F25F22]/40 bg-[#F25F22]/10 text-[#F25F22]",
  qualifying: "border-amber-400/40 bg-amber-400/10 text-amber-400",
  qualified: "border-emerald-400/40 bg-emerald-400/10 text-emerald-400",
  escalated: "border-red-500/60 bg-red-500/15 text-red-400",
  booked: "border-emerald-500/60 bg-emerald-500/15 text-emerald-300",
  completed: "border-sky-400/40 bg-sky-400/10 text-sky-300",
  estimate: "border-violet-400/40 bg-violet-400/10 text-violet-300",
};

const inputCls =
  "rounded-md border border-white/[0.1] bg-[#0B0D10] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60";

const chime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [880, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.18;
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.5);
    });
    setTimeout(() => ctx.close(), 1600);
    document.body.dataset.chime = String(Date.now());
  } catch {}
};

export default function ConversationsPanel({ token, onUnreadChange }) {
  const [convos, setConvos] = useState(null);
  const [simPhone, setSimPhone] = useState("+15550142231");
  const [replyText, setReplyText] = useState({});
  const [officeText, setOfficeText] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const prevUnread = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const load = () =>
    axios.get(`${API}/conversations`, { headers })
      .then((r) => {
        setConvos(r.data);
        const unread = r.data.filter((c) => c.office_unread).length;
        if (prevUnread.current !== null && unread > prevUnread.current) chime();
        prevUnread.current = unread;
        onUnreadChange?.(unread);
      })
      .catch(() => setConvos([]));

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const openConvo = (c) => {
    setOpen(open === c.id ? null : c.id);
    if (c.office_unread) {
      const next = (convos || []).map((x) => (x.id === c.id ? { ...x, office_unread: false } : x));
      setConvos(next);
      const remaining = next.filter((x) => x.office_unread).length;
      prevUnread.current = remaining;
      onUnreadChange?.(remaining);
      axios.post(`${API}/conversations/${c.id}/read`, {}, { headers }).catch(() => {});
    }
  };

  const simMissedCall = async () => {
    setBusy(true);
    await axios.post(`${API}/sim/missed-call`, { caller_phone: simPhone }, { headers });
    await load();
    setBusy(false);
  };

  const simReply = async (phone) => {
    const body = (replyText[phone] || "").trim();
    if (!body) return;
    setBusy(true);
    await axios.post(`${API}/sim/sms-reply`, { caller_phone: phone, body }, { headers });
    setReplyText({ ...replyText, [phone]: "" });
    await load();
    setBusy(false);
  };

  const officeReply = async (convo) => {
    const body = (officeText[convo.id] || "").trim();
    if (!body) return;
    setBusy(true);
    await axios.post(`${API}/conversations/${convo.id}/reply`, { body }, { headers });
    setOfficeText({ ...officeText, [convo.id]: "" });
    await load();
    setBusy(false);
  };

  const setStage = async (id, stage) => {
    const { data } = await axios.post(`${API}/conversations/${id}/stage`, { stage }, { headers });
    if (stage === "completed") {
      if (data.review_sent) {
        toast.success("Job completed — review request texted to the homeowner");
      } else if (data.review_link_missing) {
        toast.error("Add the customer's Google review link in the Customers tab first");
      } else {
        toast.info("Marked complete — review already requested earlier");
      }
    }
    await load();
  };

  return (
    <div data-testid="conversations-panel">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.08] bg-[#12161F] p-5">
        <PhoneMissed className="h-5 w-5 text-[#F25F22]" />
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-slate-100">Simulator</span> — demo the automation without phone lines
        </p>
        <div className="ml-auto flex items-center gap-2">
          <input
            value={simPhone}
            onChange={(e) => setSimPhone(e.target.value)}
            data-testid="sim-phone-input"
            className={inputCls}
            placeholder="+15550142231"
          />
          <button
            onClick={simMissedCall}
            disabled={busy}
            data-testid="sim-missed-call-button"
            className="flex items-center gap-2 rounded-md bg-[#F25F22] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simulate missed call"}
          </button>
          <button onClick={load} data-testid="convos-refresh" className="rounded-md border border-white/[0.12] p-2 text-slate-400 hover:text-slate-100">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {convos === null ? (
          <div className="flex items-center justify-center gap-2 p-12 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : convos.length === 0 ? (
          <div className="rounded-xl border border-white/[0.08] bg-[#12161F] p-12 text-center text-sm text-slate-500" data-testid="convos-empty">
            No conversations yet — simulate a missed call above to watch the engine work.
          </div>
        ) : (
          convos.map((c) => (
            <div key={c.id} data-testid={`convo-${c.id}`} className="rounded-xl border border-white/[0.08] bg-[#12161F] p-5">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => openConvo(c)}
                  data-testid={`convo-toggle-${c.id}`}
                  className="font-mono text-sm font-semibold text-slate-100 hover:text-[#F25F22]"
                >
                  {c.caller_phone}
                </button>
                {c.office_unread && (
                  <span data-testid={`convo-new-${c.id}`} className="flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-red-400">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-red-400" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-400" />
                    </span>
                    New
                  </span>
                )}
                <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${STAGE_STYLES[c.stage] || ""}`} data-testid={`convo-stage-${c.id}`}>
                  {c.stage.replace("_", " ")}
                </span>
                <span className="text-xs text-slate-500">{c.company_name}</span>
                {c.service_type && <span className="truncate text-xs text-slate-400">· {c.service_type}</span>}
                {!["booked", "completed"].includes(c.stage) && (
                  <button
                    onClick={() => setStage(c.id, "booked")}
                    data-testid={`convo-book-${c.id}`}
                    className="ml-auto flex items-center gap-1.5 rounded-md border border-emerald-500/30 px-3 py-1.5 text-xs font-medium text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark booked
                  </button>
                )}
                {c.stage === "booked" && (
                  <button
                    onClick={() => setStage(c.id, "completed")}
                    data-testid={`convo-complete-${c.id}`}
                    className="ml-auto flex items-center gap-1.5 rounded-md border border-sky-400/30 px-3 py-1.5 text-xs font-medium text-sky-300 hover:bg-sky-400/10"
                  >
                    <Star className="h-3.5 w-3.5" /> Mark complete + request review
                  </button>
                )}
              </div>

              {open === c.id && (
                <div className="mt-4 border-t border-white/[0.06] pt-4">
                  <div className="space-y-2.5">
                    {c.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.from === "customer" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-xl px-3.5 py-2 text-[13px] leading-relaxed ${
                          m.from === "customer"
                            ? "bg-[#F25F22] text-white"
                            : m.from === "office"
                              ? "border border-emerald-400/40 bg-emerald-400/[0.08] text-emerald-100"
                              : "border border-white/[0.08] bg-[#1A212E] text-slate-200"
                        }`}>
                          {m.from === "office" && (
                            <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-emerald-400">
                              Office
                            </span>
                          )}
                          {m.body}
                          {m.from !== "customer" && !m.delivered && (
                            <span className="ml-2 font-mono text-[9px] uppercase text-amber-400">simulated</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {c.stage !== "booked" && (
                    <div className="mt-4 space-y-2.5 border-t border-white/[0.06] pt-4">
                      <div className="flex items-center gap-2">
                        <MessageSquareText className="h-4 w-4 shrink-0 text-emerald-400" />
                        <input
                          value={officeText[c.id] || ""}
                          onChange={(e) => setOfficeText({ ...officeText, [c.id]: e.target.value })}
                          data-testid={`office-reply-input-${c.id}`}
                          placeholder="Reply to this caller as the office…"
                          className={`${inputCls} flex-1`}
                          onKeyDown={(e) => e.key === "Enter" && officeReply(c)}
                        />
                        <button
                          onClick={() => officeReply(c)}
                          disabled={busy}
                          data-testid={`office-reply-send-${c.id}`}
                          className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                        >
                          <Send className="h-3.5 w-3.5" /> Send to caller
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageSquareText className="h-4 w-4 shrink-0 text-slate-500" />
                        <input
                          value={replyText[c.caller_phone] || ""}
                          onChange={(e) => setReplyText({ ...replyText, [c.caller_phone]: e.target.value })}
                          data-testid={`convo-reply-input-${c.id}`}
                          placeholder="Simulate the caller's reply (demo)…"
                          className={`${inputCls} flex-1`}
                          onKeyDown={(e) => e.key === "Enter" && simReply(c.caller_phone)}
                        />
                        <button
                          onClick={() => simReply(c.caller_phone)}
                          disabled={busy}
                          data-testid={`convo-reply-send-${c.id}`}
                          className="flex items-center gap-1.5 rounded-md border border-[#F25F22]/40 px-3.5 py-2 text-xs font-semibold text-[#F25F22] hover:bg-[#F25F22]/10 disabled:opacity-50"
                        >
                          Simulate reply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
